'use client';

/**
 * ConnectionHealthBadge Component
 *
 * Displays the health status of an organization connection based on
 * token expiry times and validation status.
 *
 * @example
 * ```tsx
 * <ConnectionHealthBadge
 *   expiresAt="2026-01-19T12:00:00Z"
 *   validationStatus="valid"
 * />
 * ```
 */

import { Badge } from '@cortex/ui';

interface ConnectionHealthBadgeProps {
  /**
   * Whether this is a GitHub App installation (vs OAuth)
   * App installations don't have expiring tokens
   */
  isAppInstallation?: boolean;

  /**
   * When the access token expires (ISO 8601 timestamp)
   * Only relevant for OAuth connections
   */
  expiresAt?: string | undefined;

  /**
   * When the refresh token expires (ISO 8601 timestamp)
   * Only relevant for OAuth connections
   */
  refreshExpiresAt?: string | undefined;

  /**
   * Current validation status from the database
   */
  validationStatus?: string | null;
}

/**
 * Calculate health status from expiry times and validation status
 */
function getHealthStatus(
  isAppInstallation: boolean,
  expiresAt?: string,
  refreshExpiresAt?: string,
  validationStatus?: string | null
): {
  variant: 'success' | 'warning' | 'destructive';
  label: string;
} {
  // Check validation status first
  if (validationStatus === 'expired' || validationStatus === 'invalid') {
    return { variant: 'destructive', label: 'Invalid' };
  }

  if (validationStatus === 'reauth_required') {
    return { variant: 'destructive', label: 'Re-auth Required' };
  }

  if (validationStatus === 'refresh_needed') {
    return { variant: 'warning', label: 'Refresh Needed' };
  }

  // GitHub App installations don't have expiring tokens
  if (isAppInstallation) {
    return { variant: 'success', label: 'Installed' };
  }

  const now = new Date();

  // Check if access token is expired (OAuth only)
  if (expiresAt) {
    const expiryDate = new Date(expiresAt);
    const timeUntilExpiry = expiryDate.getTime() - now.getTime();
    const oneHour = 60 * 60 * 1000;

    if (timeUntilExpiry < 0) {
      return { variant: 'destructive', label: 'Expired' };
    }

    if (timeUntilExpiry < oneHour) {
      return { variant: 'warning', label: 'Expiring Soon' };
    }
  }

  // Check if refresh token is expired (OAuth only)
  if (refreshExpiresAt) {
    const refreshExpiryDate = new Date(refreshExpiresAt);
    if (refreshExpiryDate.getTime() < now.getTime()) {
      return { variant: 'destructive', label: 'Re-auth Required' };
    }
  }

  // All good
  return { variant: 'success', label: 'Connected' };
}

export function ConnectionHealthBadge({
  isAppInstallation = false,
  expiresAt,
  refreshExpiresAt,
  validationStatus,
}: ConnectionHealthBadgeProps) {
  const { variant, label } = getHealthStatus(
    isAppInstallation,
    expiresAt,
    refreshExpiresAt,
    validationStatus
  );

  return <Badge variant={variant}>{label}</Badge>;
}
